import moment from "moment";

import { PredefinedRuleSet, PredefinedRuleSetItem } from "@/Appointments/Api/Dto/Responses/PredefinedRuleSet";
import { PatientPopulationBasedFundingStatus } from "@/Common/Models/Appointments/PopulationBasedFundiing/PatientPopulationBasedFundingStatus";
import { QuestionnaireStatusInfo } from "@/Common/Models/Questionnaires/QuestionnaireStatusInfo";
import { ServiceComboTargetClientele } from "@/Common/Models/Services/Enums/ServiceComboTargetClientele";
import { ThirdPartySyncStatus } from "@/Common/Models/ThirdPartySyncStatus";
import { ThirdPartySyncDisplayState } from "../ThirdPartySyncDisplayState";

import img_icon_pictogrammemyle_emploisdutemps_assigne from "@/Globals/_images/icons/admin/PictogrammeMYLE-EmploisDuTemps-Assigne.png";
import img_icon_pictogrammemyle_emploisdutemps_clinique from "@/Globals/_images/icons/admin/PictogrammeMYLE-EmploisDuTemps-Clinique.png";
import img_icon_pictogrammemyle_emploisdutemps_organization from "@/Globals/_images/icons/admin/PictogrammeMYLE-EmploisDuTemps-Organization.png";
import img_icon_pictogrammemyle_emploisdutemps_ouvert from "@/Globals/_images/icons/admin/PictogrammeMYLE-EmploisDuTemps-Ouvert.png";
import img_icon_pictogrammemyle_emploisdutemps_rdvonline from "@/Globals/_images/icons/admin/PictogrammeMYLE-EmploisDuTemps-RDVOnline.png";
import img_icon_pictogrammemyle_emploisdutemps_rdvurgent from "@/Globals/_images/icons/admin/PictogrammeMYLE-EmploisDuTemps-RDVUrgent.png";
import img_icon_pictogrammemyle_emploisdutemps_site from "@/Globals/_images/icons/admin/PictogrammeMYLE-EmploisDuTemps-Site.png";

class CalendarAppointmentDisplay {
    public id: string;    // mandatory kendo scheduler field
    public start: Date;   // mandatory kendo scheduler field
    public end: Date;     // mandatory kendo scheduler field
    public title: string; // mandatory kendo scheduler field

    public AppointmentId: string;
    public AppointmentItemId: string;
    public AppointmentStatusCode: string;
    public AppointmentStatusLookupId: string;
    public AppointmentComboId: string;
    public IsSharedWithPortal: boolean;
    public PortalDisplayClass: string;
    public ComboId: number;
    public ComboName: string;
    public ServiceDefinitionId: string;
    public ServiceDefinitionName: string;
    public AppointmentStatus: string;
    public AppointmentSubject: string;
    public AppointmentServiceName: string;
    public AppointmentDescription: string;
    public AppointmentDescriptionWithoutCombo: string;

    public EmployeeContactFacetId: string;

    public Room: string;
    public PatientId: string;
    public PatientContactId: string;
    public PatientName: string;
    public PatientNAM: string;
    public PatientIdentifier: string;
    public SiteCode: string;
    public SiteName: string;
    public SiteId: string;
    public ContactPoints: string[];
    public CanadianTriageAndAcuityScore: number;

    // for month view
    public IsPredefined: boolean;
    public NbAppointments: number;
    public NbAvailabilities: number;
    public MaxRulesetAvailability: number;
    public NbAppointmentFromPredefined: number;

    public IsPaid: string;
    public IsTeleconsultation: boolean;
    public IsOnlineBookingAllowed: boolean;
    public TargetedClientName: string;

    public IsRuleSet: boolean;
    public RuleSetItems: PredefinedRuleSetItem[];
    public RuleSetItemsDisplay: PredefinedRuleSetItem[];
    public RuleSet: PredefinedRuleSet;
    public RuleSetExtraClass: string;

    public QuestionnaireStatusInfo: QuestionnaireStatusInfo;

    public IsQuebecHubBookedAppointment: boolean;
    public QuebecBookingHubDisplayState: ThirdPartySyncDisplayState;
    public QuebecBookingHubStatus: ThirdPartySyncStatus;
    public QuebecBookingHubLastUpdated: Date;

    // ------------- Appointment Event Feeds from Third-party Systems. (Myle is mirroring an Third-party system calendar)
    public IsReadOnly: boolean;
    public PatientPopulationBasedFundingStatus: PatientPopulationBasedFundingStatus;
    public PatientProviderContactFacetId: string;

    public IsSynchronized: boolean;
    public HasPatient: boolean;

    constructor() {
        this.id = "";
        this.title = "";
        this.AppointmentId = "";

        this.AppointmentItemId = "";
        this.AppointmentComboId = "";
        this.AppointmentStatusCode = "";
        this.AppointmentStatusLookupId = "";
        this.AppointmentStatus = "";
        this.AppointmentSubject = "";
        this.AppointmentServiceName = "";
        this.AppointmentDescription = "";
        this.AppointmentDescriptionWithoutCombo = "";
        this.EmployeeContactFacetId = "";
        this.IsSharedWithPortal = false;
        this.ComboId = null;
        this.Room = "";
        this.PatientId = "";
        this.PatientContactId = "";
        this.PatientName = "";
        this.PatientNAM = "";
        this.PatientIdentifier = "";
        this.SiteCode = "";
        this.SiteName = "";
        this.SiteId = "";
        this.ContactPoints = [];
        this.IsPredefined = false;
        this.IsPaid = "";
        this.IsTeleconsultation = false;

        this.IsRuleSet = false;
        this.RuleSetItems = [];

        this.PatientPopulationBasedFundingStatus = null;
        this.PatientProviderContactFacetId = "";
    }

    public FromRuleSet(ruleSet: PredefinedRuleSet): void {
        if (ruleSet != null) {
            this.id = ruleSet.ID;
            this.start = moment(ruleSet.StartDate).toDate();
            this.end = moment(ruleSet.EndDate).toDate();
            this.EmployeeContactFacetId = ruleSet.EmployeeId;
            this.AppointmentStatusCode = "PREDEFINEDRULESET";

            if (ruleSet.PredefinedRuleSetItems != null && ruleSet.PredefinedRuleSetItems.length > 0) {
                this.FromRuleSetItems(ruleSet.PredefinedRuleSetItems);
            }

            this.RuleSet = ruleSet;
        }
    }

    public FromRuleSetItems(ruleSetItems: PredefinedRuleSetItem[]): void {
        if (ruleSetItems != null) {
            if (ruleSetItems.length > 0) {
                this.RuleSetItemsDisplay = [];
                this.IsRuleSet = true;

                var distinctServices = ruleSetItems.toImmutable().groupBy(x => x.ServiceDefinitionId);

                distinctServices.forEach((group: Immutable.List<PredefinedRuleSetItem>) => {
                    var x = group.get(0);

                    var sumMin = 0;
                    group.toArray().forEach(x => {
                        sumMin += x.Min;
                    });

                    var sumMinDisplay = sumMin == 0 ? "-" : sumMin.toString();

                    var groupMax = group.toArray()[0].GroupMax;

                    // if we only have one service in group and groupMax is zero, take the max from the service
                    if (group.count() == 1 && groupMax == 0)
                        groupMax = group.toArray()[0].Max;

                    var denominatorDisplay = "";
                    denominatorDisplay = (groupMax == 0) ? "-" : groupMax.toString();
                    denominatorDisplay = (groupMax == 0) ? "-" : groupMax.toString();

                    var counters = sumMinDisplay + " | " + denominatorDisplay;

                    var displayDuration = "";
                    let hasStandalone = group.filter(z => z.ComboId == null).count() > 0;
                    let standAloneMarker = hasStandalone ? "+" : "";

                    var nbCombos = group.filter(z => z.ComboId != null).count();

                    if (nbCombos >= 1)
                        displayDuration = " (" + standAloneMarker + nbCombos + (nbCombos > 1 ? " Combos)" : " Combo)");
                    else
                        displayDuration = " (" + x.DurationMinutes + " " + cacheStore.GetString("MinuteLLabel") + ") ";

                    x.DisplayName = cacheStore.GetLocalizedValueByLookupId(x.ServiceNameLookupId);
                    x.DisplayDuration = displayDuration;

                    x.DisplayCounters = counters;

                    var isAvailableOnline = group.some(x => x.IsOnlineBookingAllowed);
                    var isUrgent = group.some(x => x.IsUrgent);
                    var isOpenClientele = group.some(x => x.TargetedClient == ServiceComboTargetClientele.Open);
                    const isClinicalClientele = group.some(x => x.TargetedClient == ServiceComboTargetClientele.Clinic);
                    const isSiteClientele = group.some(x => x.TargetedClient == ServiceComboTargetClientele.Site);
                    const isOrganizationClientele = group.some(x => x.TargetedClient == ServiceComboTargetClientele.Organization);
                    var isAssignedClientele = group.some(x => x.TargetedClient == ServiceComboTargetClientele.AssignedClientele);

                    x.DisplayImages = "<div class=\"flex-container flex-start\">";

                    if (isAvailableOnline)
                        x.DisplayImages += ` <div class="img-placeholder"><img src="${img_icon_pictogrammemyle_emploisdutemps_rdvonline}" style="margin-top: 0px; margin-right: 0px; height: 30px;" /></div>`;

                    if (isUrgent)
                        x.DisplayImages += ` <div class="img-placeholder"><img src="${img_icon_pictogrammemyle_emploisdutemps_rdvurgent}" style="margin-top: 0px; margin-right: 0px; height: 30px;" /></div>`;

                    const openImg = isOpenClientele ? `<img src="${img_icon_pictogrammemyle_emploisdutemps_ouvert}"  />` : "";
                    x.DisplayImages += ` <div class="img-placeholder" style="height: 20px; margin-top:5px">${openImg}</div>`;

                    const clinicImg = isClinicalClientele ? `<img src="${img_icon_pictogrammemyle_emploisdutemps_clinique}"  />` : "";
                    x.DisplayImages += ` <div class="img-placeholder" style="height: 20px; margin-top:5px">${clinicImg}</div>`;

                    const siteImg = isSiteClientele ? `<img src="${img_icon_pictogrammemyle_emploisdutemps_site}" />` : "";
                    x.DisplayImages += ` <div class="img-placeholder" style="height: 20px; margin-top:5px">${siteImg}</div>`;

                    const orgImg = isOrganizationClientele ? `<img src="${img_icon_pictogrammemyle_emploisdutemps_organization}" />` : "";
                    x.DisplayImages += ` <div class="img-placeholder" style="height: 20px; margin-top:5px">${orgImg}</div>`;

                    const assignImg = isAssignedClientele ? `<img src="${img_icon_pictogrammemyle_emploisdutemps_assigne}" />` : "";
                    x.DisplayImages += ` <div class="img-placeholder" style="height: 20px; margin-top:5px">${assignImg}</div>`;

                    x.DisplayImages += "</div>";

                    this.RuleSetItemsDisplay.push(x);
                });
            }
        }
    }
}

export { CalendarAppointmentDisplay };
